import { useEffect, useState } from 'react';
import {
  useTranslate,
  useNotification,
  useCustomMutation,
} from '@refinedev/core';
import { Modal, Form, Input, InputNumber, Select } from 'antd';
import { extractBackendMessage } from '@/utils/apiError';
import type {
  HelloNotificationIn,
  UsageLimitReachedNotificationIn,
} from '@/api/generated/schemas';

type NotifType = 'hello' | 'usage_limit_reached';

type FormValues = {
  type: NotifType;
  title?: string;
  body?: string;
  image_url?: string;
  deeplink?: string;
  account_id?: number;
  consumed_seconds?: number;
  limit_seconds?: number;
};

interface SendPushNotificationModalProps {
  open: boolean;
  onClose: () => void;
  userId: number;
}

export const SendPushNotificationModal = ({
  open,
  onClose,
  userId,
}: SendPushNotificationModalProps) => {
  const t = useTranslate();
  const { open: notify } = useNotification();
  const { mutateAsync } = useCustomMutation();
  const [form] = Form.useForm<FormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [notifType, setNotifType] = useState<NotifType>('hello');

  const tk = (suffix: string) => t(`iam.users.sendPushModal.${suffix}`);

  useEffect(() => {
    if (open) {
      setNotifType('hello');
      form.setFieldsValue({ type: 'hello' });
    } else {
      form.resetFields();
      setSubmitting(false);
    }
  }, [open, form]);

  const handleSubmit = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const notification =
      values.type === 'hello'
        ? ({
            type: 'hello',
            title: values.title,
            body: values.body,
            image_url: values.image_url || null,
            deeplink: values.deeplink || null,
          } as HelloNotificationIn)
        : ({
            type: 'usage_limit_reached',
            account_id: values.account_id,
            consumed_seconds: values.consumed_seconds,
            limit_seconds: values.limit_seconds,
            deeplink: values.deeplink || null,
          } as UsageLimitReachedNotificationIn);

    setSubmitting(true);
    try {
      await mutateAsync({
        url: `iam/users/${userId}/notify`,
        method: 'post',
        values: { notification },
        successNotification: false,
        errorNotification: false,
      });
      notify?.({
        type: 'success',
        message: t('notifications.success'),
        description: tk('success'),
      });
      onClose();
    } catch (error) {
      const description = extractBackendMessage(error) ?? tk('error_fallback');
      notify?.({
        type: 'error',
        message: t('notifications.error'),
        description,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={tk('title')}
      okText={tk('submit')}
      okButtonProps={{ loading: submitting }}
      cancelText={t('actions.cancel')}
      cancelButtonProps={{ disabled: submitting }}
      closable={!submitting}
      maskClosable={!submitting}
      onCancel={onClose}
      onOk={handleSubmit}
      destroyOnClose
      width={520}
    >
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        disabled={submitting}
        initialValues={{ type: 'hello' }}
      >
        <Form.Item name="type" label={tk('fields.type')}>
          <Select
            options={[
              { label: tk('types.hello'), value: 'hello' },
              {
                label: tk('types.usage_limit_reached'),
                value: 'usage_limit_reached',
              },
            ]}
            onChange={(val: NotifType) => setNotifType(val)}
          />
        </Form.Item>

        {notifType === 'hello' && (
          <>
            <Form.Item name="title" label={tk('fields.title')}>
              <Input placeholder="Hello!" />
            </Form.Item>
            <Form.Item name="body" label={tk('fields.body')}>
              <Input.TextArea rows={3} placeholder="Test notification." />
            </Form.Item>
            <Form.Item name="image_url" label={tk('fields.image_url')}>
              <Input placeholder="https://..." />
            </Form.Item>
            <Form.Item name="deeplink" label={tk('fields.deeplink')}>
              <Input placeholder="novelist://..." />
            </Form.Item>
          </>
        )}

        {notifType === 'usage_limit_reached' && (
          <>
            <Form.Item
              name="account_id"
              label={tk('fields.account_id')}
              rules={[
                { required: true, message: tk('errors.account_id_required') },
              ]}
            >
              <InputNumber style={{ width: '100%' }} min={1} precision={0} />
            </Form.Item>
            <Form.Item
              name="consumed_seconds"
              label={tk('fields.consumed_seconds')}
              rules={[
                {
                  required: true,
                  message: tk('errors.consumed_seconds_required'),
                },
              ]}
            >
              <InputNumber style={{ width: '100%' }} min={0} precision={0} />
            </Form.Item>
            <Form.Item
              name="limit_seconds"
              label={tk('fields.limit_seconds')}
              rules={[
                {
                  required: true,
                  message: tk('errors.limit_seconds_required'),
                },
              ]}
            >
              <InputNumber style={{ width: '100%' }} min={0} precision={0} />
            </Form.Item>
            <Form.Item name="deeplink" label={tk('fields.deeplink')}>
              <Input placeholder="novelist://..." />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
};
